/** Planet views use their own ellipsoid and release the WebGL context on close. */
import { useEffect, useRef, useState } from 'react';
import { TextButton } from '@/components/ui/primitives';
import { getGlobeEngine } from '../../globe/engineHandle';
import type { PlanetBody, PlanetImagery } from '../../globe/moonMars';

export function PlanetPanel({ body, onClose }: { body: PlanetBody; onClose?: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imagery, setImagery] = useState<PlanetImagery>(body === 'mars' ? 'detail' : 'natural');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let disposed = false;
    let engine: ReturnType<typeof getGlobeEngine>;
    const timer = window.setInterval(() => {
      engine = getGlobeEngine();
      if (!engine || !ref.current) return;
      window.clearInterval(timer);
      const show = body === 'moon' ? engine.showMoon(ref.current) : engine.showMars(ref.current);
      show
        .then(() => {
          if (!disposed) setReady(true);
        })
        .catch((err: unknown) => {
          if (!disposed) setError(String(err));
        });
    }, 100);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      if (body === 'moon') engine?.hideMoon();
      else engine?.hideMars();
    };
  }, [body]);
  const changeImagery = async (mode: PlanetImagery) => {
    setLoading(true);
    setError(null);
    setImagery(mode);
    try {
      await getGlobeEngine()?.setPlanetImagery?.(body, mode);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setImagery('natural');
    } finally {
      setLoading(false);
    }
  };
  const landmarks =
    body === 'mars'
      ? [
          { name: 'Jezero / Perseverance', lon: 77.4509, lat: 18.4446, height: 35000 },
          { name: 'Gale / Curiosity', lon: 137.4417, lat: -4.5895, height: 12000 },
          { name: 'Olympus Mons', lon: -133.8, lat: 18.65, height: 1000000 },
        ]
      : [
          { name: 'Near side', lon: 0, lat: 0, height: 4500000 },
          { name: 'Far side', lon: 180, lat: 0, height: 4500000 },
          { name: 'Tycho crater', lon: -11.36, lat: -43.31, height: 160000 },
        ];
  return (
    <div className="relative h-full w-full bg-canvas" data-testid={`${body}-surface`} data-ready={ready}>
      <div ref={ref} className="absolute inset-0" />
      <div className="absolute left-3 right-3 top-3 flex flex-wrap items-center gap-2 rounded-panel border border-hairline bg-raised/95 p-2 text-xs">
        {onClose && <TextButton onClick={onClose}>Back to Earth</TextButton>}
        <strong className="mr-2">{body === 'moon' ? 'Moon' : 'Mars'} surface</strong>
        <select
          aria-label="Planet imagery"
          value={imagery}
          disabled={!ready || loading}
          onChange={(e) => void changeImagery(e.target.value as PlanetImagery)}
          className="rounded-control border border-hairline bg-inset p-1"
        >
          <option value="natural">
            {body === 'moon' ? 'LROC · global imagery' : 'Viking · global colour'}
          </option>
          {body === 'mars' && <option value="detail">CTX · high resolution + Gale HiRISE</option>}
          <option value="terrain">Original 3D terrain (ion)</option>
        </select>
        {landmarks.map((p) => (
          <TextButton
            key={p.name}
            disabled={!ready}
            onClick={() => getGlobeEngine()?.flyToPlanet?.(body, p.lon, p.lat, p.height)}
          >
            {p.name}
          </TextButton>
        ))}
        <TextButton
          disabled={!ready}
          onClick={() => getGlobeEngine()?.flyToPlanet?.(body, 0, 10, body === 'moon' ? 4700000 : 9200000)}
        >
          Whole globe
        </TextButton>
      </div>
      {(!ready || loading || error) && (
        <div role="status" className="absolute left-3 top-28 max-w-md rounded-panel bg-raised/95 p-3 text-xs">
          {error ?? (loading ? 'Loading terrain…' : 'Opening planet…')}
        </div>
      )}
      <div className="pointer-events-none absolute bottom-10 right-3 max-w-sm rounded-panel bg-raised/90 px-3 py-2 text-[10px] text-ink-2">
        {body === 'mars'
          ? 'CTX ≈5 m/pixel; finer HiRISE imagery near Curiosity. Landing-site markers are reference locations. Surface relief requires the optional 3D terrain.'
          : 'LRO LROC WAC global imagery ≈100 m/pixel. Orion follows the historical mission clock. Surface relief requires the optional 3D terrain.'}
        <br />
        Drag to turn · scroll to zoom · shift-drag to tilt
      </div>
    </div>
  );
}
